import React, { useState, useEffect } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import {motion} from "framer-motion";
import Header from "../components/header";
import { useNavigate } from "react-router-dom";
import { NavLink } from 'react-router-dom';
import bikedealer from "../images/ridley-dealers.png";
import ridleyImage from "../images/ridley.png"; // Import Ridley image for fallbacks
import { builder } from '../utils/builderWithLocale';
import InstagramPosts from '../components/InstagramPosts';
import BikeCategory from '../components/BikeCategory';
import NovinkyItem from '../components/NovinkyItem';
import { mockBikeCategories, mockNewsArticles, mockHomeContent } from '../utils/mockData';
import imagePreloadCoordinator from '../utils/imagePreloadCoordinator';
import imageDebugUtils from '../utils/imageDebugUtils';
import { getLocalizedText } from '../utils/textLocalization';
import EditableField from '../editry/EditableField';

// Preload all home page data including header and footer
export const preloadHomeData = () => {
  try {
    // Check if Builder.io API key is initialized
    if (!builder.apiKey) {
      console.error("Builder.io API key is not set. Make sure builder.init() is called before preloadHomeData()");
      builder.init("425a1e92273943bab5fbaee91f6e709e"); // Re-init just in case
      
      if (!builder.apiKey) {
        return {
          homeContent: Promise.resolve({"data": {}}),
          novinkyContent: Promise.resolve([]),
          pick4Content: Promise.resolve([]),
          headerContent: Promise.resolve({}),
          footerContent: Promise.resolve({})
        };
      }
    }
    
    // Use consistent options for API requests
    const apiOptions = { 
      cachebust: new Date().getTime(), // Force fresh data with timestamp
      cache: false, // Disable cache to ensure fresh data 
      cacheTime: 0, // No cache time
      noTargeting: true, // Disable targeting to ensure we get data
      includeRefs: true, // Include references
      apiKey: "425a1e92273943bab5fbaee91f6e709e" // Explicitly include API key
    };


    /*
    // Log each request to help debug
    console.log("Requesting Builder.io data with API key:", builder.apiKey);
    */


    // Return promises for all required content
    return {
      homeContent: builder.get("home-kola", apiOptions)
        .then(data => {
          //console.log("home-kola data received:", data ? "success" : "empty");
          return data || {"data": {}};
        })
        .catch(error => {
          console.error("Error fetching home-kola:", error);
          return {"data": {}};
        }),
      
      novinkyContent: builder.getAll("novinky", {
        ...apiOptions,
        limit: 5, // Request more items than needed
        fields: 'data.image,data.tag,data.title,data.description,data.route', // Specify fields
        options: { enrich: true } // Enrich the response
      })
        .then(data => {
          //console.log("novinky data received:", data && data.length ? `${data.length} items` : "empty", data);
          return data || [];
        })
        .catch(error => {
          console.error("Error fetching novinky:", error);
          return [];
        }),
        
      pick4Content: builder.getAll("pick-4", {
        ...apiOptions,
        cache: false, // Ensure we get fresh data for pick-4
        limit: 10, // Request more items to ensure we get all
        fields: 'data.cesta,data.nazev,data.popis,data.obrazek', // Specify fields
        options: { enrich: true } // Enrich the response
      })
        .then(data => {
          //console.log("pick-4 data received:", data && data.length ? `${data.length} items` : "empty", data);
          return data || [];
        })
        .catch(error => {
          console.error("Error fetching pick-4:", error);
          return [];
        }),
        
      headerContent: builder.get("header", apiOptions)
        .then(data => {
          //console.log("header data received:", data ? "success" : "empty");
          return data || {};
        })
        .catch(error => {
          console.error("Error fetching header:", error);
          return {};
        }),
        
      footerContent: builder.get("footer", apiOptions)
        .then(data => {
          //console.log("footer data received:", data ? "success" : "empty");
          return data || {};
        })
        .catch(error => {
          console.error("Error fetching footer:", error);
          return {};
        })
    };
  } catch (error) {
    console.error("Error in preloadHomeData:", error);
    // Return default empty values to prevent breaks
    return {
      homeContent: Promise.resolve({"data": {}}),
      novinkyContent: Promise.resolve([]),
      pick4Content: Promise.resolve([]),
      headerContent: Promise.resolve({}),
      footerContent: Promise.resolve({})
    };
  }
};

function App({kolaRef, scrollTo}) {
    const navigate = useNavigate();

    // Get localized text
    const dealersTitleText = getLocalizedText('home.dealers_title');
    const dealersSubtitleText = getLocalizedText('home.dealers_subtitle');
    const dealersButtonText = getLocalizedText('home.dealers_button');
    const newsTitleText = getLocalizedText('home.news_title');
    const moreArticlesText = getLocalizedText('home.more_articles');

    const [homeContent, setHomeContent] = useState({"data": []});
    const [novinkyContent, setNovinkyContent] = useState();
    const [pick4Content, setPick4Content] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        document.title = getLocalizedText('home.page_title');
        
        // Check if we're returning to the home page after navigation
        const isReturningToHome = imagePreloadCoordinator.isReturningToHome();
            
        if (isReturningToHome) {
            console.log("Returning to home page - checking image cache status");
            
            // Clear the flag so we don't do this again until next return
            imagePreloadCoordinator.clearReturningFlag();
            
            // Log the cache status for debugging
            imageDebugUtils.logImageCacheStatus();
            
            // Our components will handle preloading any missing images
            // with the improved caching system
        }
        
        // Add clear console logs to track data loading
        //console.log("Home component mounted, checking for preloaded data");
        
        // Function to handle data loading success
        const handleDataSuccess = (homeData, novinkyData, pick4Data) => {
          console.log("Setting home data:", 
            "homeData:", homeData ? "available" : "missing", 
            "novinkyData:", novinkyData && novinkyData.length ? `${novinkyData.length} items` : "missing", 
            "pick4Data:", pick4Data && pick4Data.length ? `${pick4Data.length} items` : "missing"
            
          );
          
          // Validate homeData structure
          let validHomeData = homeData;
          if (!homeData || !homeData.data) {
            console.warn("Invalid homeData structure, using default");
            validHomeData = {"data": {}};
          }
          
          // Ensure data is set with proper validation
          setHomeContent(validHomeData);
          setNovinkyContent(Array.isArray(novinkyData) ? novinkyData : []);
          setPick4Content(Array.isArray(pick4Data) ? pick4Data : []);
          
          // Debug the pick4Data to see why it might be empty
          if (!pick4Data || !Array.isArray(pick4Data) || pick4Data.length === 0) {
            console.warn("pick4Data is empty or invalid. This will cause the bike categories to not show.");
          } else {
            /*
            console.log("pick4Data details:", pick4Data.map(item => ({
              id: item.id,
              hasData: !!item.data,
              keys: item.data ? Object.keys(item.data) : []
            })));
            */
          }
          
          setIsLoading(false);
        };
        
        if (window.preloadedHomeData && 
            window.preloadedHomeData.homeData && 
            window.preloadedHomeData.novinkyData && 
            window.preloadedHomeData.pick4Data) {
          
          const { homeData, novinkyData, pick4Data } = window.preloadedHomeData;
          //console.log("SUCCESS: Using preloaded data for home page");
          
          // Check if we're returning to the home page
          const isReturningToHome = typeof sessionStorage !== 'undefined' && 
            sessionStorage.getItem('returningToHome') === 'true';
          
          // If returning to home, clone the data to force React to re-render
          if (isReturningToHome) {
            console.log("Returning to home page - refreshing data objects");
            
            // Deep clone with JSON parse/stringify to force new object references
            // This will cause React to re-render even if the content is identical
            const clonedHomeData = JSON.parse(JSON.stringify(homeData));
            const clonedNovinkyData = JSON.parse(JSON.stringify(novinkyData));
            const clonedPick4Data = JSON.parse(JSON.stringify(pick4Data));
            
            // Handle the data with cloned objects
            handleDataSuccess(clonedHomeData, clonedNovinkyData, clonedPick4Data);
            setIsLoading(false);
            return;
          }
          
          // Set data immediately to prevent flash of loading content
          handleDataSuccess(homeData, novinkyData, pick4Data);
          setIsLoading(false);
          return;
        } else {
          console.warn("No preloaded data found or incomplete data, fetching fresh data");
          
          try {
            const preloaded = preloadHomeData();
            
            Promise.all([
              preloaded.homeContent,
              preloaded.novinkyContent,
              preloaded.pick4Content
            ]).then(([homeData, novinkyData, pick4Data]) => {
              //console.log("SUCCESS: Fresh data fetched for home page");
              handleDataSuccess(homeData, novinkyData, pick4Data);
            }).catch(error => {
              console.error("ERROR: Failed to fetch content:", error);
              // Use mock data on API failure
              console.warn("Using mock data as a fallback after API failure");
              setHomeContent(mockHomeContent);
              setNovinkyContent(mockNewsArticles);
              setPick4Content(mockBikeCategories);
              setIsLoading(false);
            });
          } catch (error) {
            console.error("ERROR: Exception in data fetching:", error);
            // Use mock data on exception
            console.warn("Using mock data as a fallback after exception");
            setHomeContent(mockHomeContent);
            setNovinkyContent(mockNewsArticles);
            setPick4Content(mockBikeCategories);
            setIsLoading(false);
          }
        }
        
        // Add a safety timeout to ensure the loading state is cleared
        const safetyTimeout = setTimeout(() => {
          if (isLoading) {
            console.warn("Safety timeout triggered - forcing loading state to complete");
            
            // If we still don't have content, provide mock data values
            console.warn("Using mock data as a fallback");
            setHomeContent(prevContent => prevContent && Object.keys(prevContent.data || {}).length > 0 ? prevContent : mockHomeContent);
            setNovinkyContent(prevContent => Array.isArray(prevContent) && prevContent.length > 0 ? prevContent : mockNewsArticles);
            setPick4Content(prevContent => Array.isArray(prevContent) && prevContent.length > 0 ? prevContent : mockBikeCategories);
            setIsLoading(false);
          }
        }, 800); // Reduced to 800ms for faster experience
        
        return () => clearTimeout(safetyTimeout);
    }, [isLoading]);

    const hrefTo = (link) => {
        navigate(link);
    }
/*
    // API logging for diagnostic purposes
    console.log('%c Builder.io API Status', 'background: #333; color: #bada55; padding: 2px;');
    console.log('API key set:', !!builder.apiKey);
    console.log('Builder config:', builder);
*/  
    // Try to fetch data directly with a simpler query if still having issues
    //console.log('Attempting a direct Builder.io API query for diagnostics');
    builder.get('home-kola', { apiKey: "425a1e92273943bab5fbaee91f6e709e", cachebust: new Date().getTime() })
      .then(result => console.log('Direct Builder.io API query result:', result ? 'Success' : 'Empty'))
      .catch(err => console.error('Direct Builder.io API query error:', err));
    
    // Show a loading indicator only when we're definitely still loading
    if (isLoading) {
        return (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            
          </div>
        );
    }
    
    // Initialize default values for null data to prevent rendering errors
    const safeHomeContent = homeContent || {"data": {}};
    
    // Helper function to safely extract string values from Builder.io content
    const safeString = (value, fallback = '') => {
      if (typeof value === 'string') return value;
      if (typeof value === 'object' && value !== null) {
        // Handle Builder.io rich text objects
        if (value.Default) return value.Default;
        if (value['@type'] === 'string' && value.value) return value.value;
        // Try to extract text content if it's a complex object
        if (value.text) return value.text;
        if (value.content) return value.content;
      }
      return fallback;
    };

    // Safely filter and validate novinky content
    const safeNovinkyContent = Array.isArray(novinkyContent) && novinkyContent.length > 0 
      ? novinkyContent.filter(item => {
          // Ensure item has proper structure and all required fields are extractable
          if (!item || !item.data) return false;
          
          // Check if title can be safely extracted
          const title = safeString(item.data.title);
          if (!title) return false;
          
          // Filter out items with title "ignore"
          if (title === 'ignore') return false;
          
          return true;
        })
      : [];
    
    // Safely filter and validate pick4 content
    const safePick4Content = Array.isArray(pick4Content) && pick4Content.length > 0 
      ? pick4Content.filter(item => {
          // Ensure item has proper structure and all required fields are extractable
          if (!item || !item.data) return false;
          
          // Check if required fields can be safely extracted
          const cesta = safeString(item.data.cesta);
          const nazev = safeString(item.data.nazev);
          if (!cesta || !nazev) return false;
          
          return true;
        })
      : [];
      /*
    // Debug why data might be missing
    console.log("Rendering home with:", 
      "homeContent keys:", Object.keys(safeHomeContent.data || {}),
      "novinkyContent:", safeNovinkyContent.length,
      "pick4Content:", safePick4Content.length
    );
    */
    
    // Debug Builder.io object structure to catch any problematic objects
    if (process.env.NODE_ENV === 'development') {
      // Check for problematic objects in home content
      Object.entries(safeHomeContent.data || {}).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && (value['@type'] || value.Default)) {
          console.warn(`Found Builder.io object in homeContent.${key}:`, value);
        }
      });
    }
    // Create placeholder novinky if needed
    const placeholderNovinky = safeNovinkyContent.length === 0 ? [
      {
        data: {
          image: ridleyImage, // Use imported image
          tag: "Aktualita",
          title: "Nová kolekce kol Ridley",
          description: "Představujeme novou kolekci kol Ridley pro sezónu 2025. Přijďte se podívat na nejnovější modely."
        }
      },
      {
        data: {
          image: ridleyImage, // Use imported image
          tag: "Závody",
          title: "Úspěchy týmu Ridley",
          description: "Jezdci týmu Ridley dosáhli skvělých výsledků v letošní sezóně závodů."
        }
      },
      {
        data: {
          image: ridleyImage, // Use imported image
          tag: "Technologie",
          title: "Inovace v konstrukci rámů",
          description: "Nové technologie použité při výrobě rámů kol Ridley přináší vyšší pevnost a nižší hmotnost."
        }
      }
    ] : [];
    
    // If pick4Content is empty, we need placeholder content
    const placeholderBikeCategories = safePick4Content.length === 0 ? [
      {
        data: {
          cesta: "gravel",
          nazev: "Gravel",
          popis: "Kola pro rychlou jízdu v terénu",
          obrazek: ridleyImage // Use imported image
        }
      },
      {
        data: {
          cesta: "road",
          nazev: "Road",
          popis: "Silniční kola pro maximální rychlost",
          obrazek: ridleyImage // Use imported image
        }
      },
      {
        data: {
          cesta: "adventure",
          nazev: "Adventure",
          popis: "Dobrodružná kola pro každý terén",
          obrazek: ridleyImage // Use imported image
        }
      },
      {
        data: {
          cesta: "mountain",
          nazev: "Mountain",
          popis: "Horská kola pro náročný terén",
          obrazek: ridleyImage // Use imported image
        }
      }
    ] : [];

  return (
    <>
    <Header scrollTo={scrollTo}/>
    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>


    <div id="kola">
        <div className="container2 barlow-bold animated scrollto clearfix pointer" id="menu-kola" ref={kolaRef}>
        {/* Use either real data or placeholders */}
        {(safePick4Content.length > 0 ? safePick4Content : placeholderBikeCategories).map((pick, index) => (
          <BikeCategory 
            key={index}
            pick={pick}
            index={index}
            hrefTo={hrefTo}
            ridleyImage={ridleyImage}
          />
        ))}
        </div>
    </div>

    <section className="text-center scrollto clearfix ">
        <div className="row clearfix">

            <div className="section-heading">
                <h3 className="barlow-semibold small-font-dark">
                  <EditableField model="home-kola" item={safeHomeContent} field="novinkyTitle">
                    {safeString(safeHomeContent.data?.novinkyTitle, newsTitleText)}
                  </EditableField>
                </h3>
                <h2 className="section-title white-title">
                  <EditableField model="home-kola" item={safeHomeContent} field="novinkyDesc">
                    {safeString(safeHomeContent.data?.novinkyDesc, "Aktuální články")}
                  </EditableField>
                </h2>
            </div>            <div className="novinky-container">
                {/* Use actual novinky content or placeholders */}
                {(safeNovinkyContent.length > 0 ? safeNovinkyContent : placeholderNovinky).slice(0, 3).map((novinka, index) => (
                  <NovinkyItem 
                    key={index}
                    novinka={novinka}
                    index={index}
                    ridleyImage={ridleyImage}
                  />
                ))}
            </div>
            <button className="articles-more" onClick={() => navigate("/articles")}><span>{moreArticlesText}</span></button>
        </div>
    </section>

    <InstagramPosts />

    <section id="prodejci" className="text-center scrollto clearfix " style={{ marginBottom: '120px' }}>
      <NavLink to="/prodejci">
      <div className="home-bike-dealer">
          <div className="home-bike-prodejci">
              <h1>
                <EditableField model="home-kola" item={safeHomeContent} field="homeBikeProdejci1">
                  {safeString(safeHomeContent.data?.homeBikeProdejci1, dealersTitleText)}
                </EditableField>
              </h1>
              <h2>
                <EditableField model="home-kola" item={safeHomeContent} field="homeBikeProdejci2">
                  {safeString(safeHomeContent.data?.homeBikeProdejci2, dealersSubtitleText)}
                </EditableField>
              </h2>
              <button><label>{dealersButtonText}</label></button>
          </div>
          <img src={bikedealer} alt="Ridley bike dealers" />
      </div>
      </NavLink>
    </section>
    </motion.div>
    </>
  );
}

export default App;